import type { AppLocationCodec } from "@/navigation/appLocation";
import type { ExtendedAssetInfo } from "@/components/playground/hooks/useAssetManager";
import type { ChartSettings } from "@/components/playground/hooks/usePlaygroundSettings";
import { PlaygroundType } from "@/services/api/widget/types";
import type { StatisticType } from "@/components/WidgetConfig";

/**
 * Playground URL / OS window location SSOT.
 *
 * OS window params (`urlMapping`, OsAppRegistry): `assets`, `layout`, `from` — enough to
 * open a window with pre-selected assets and a layout hint.
 *
 * Classic `/playground` URLs also support chart/settings query params (`type`, `period`,
 * `display`, `rebase`, `frequency`, `statistic`, `window`, `start_date`, `end_date`,
 * `exposure_display`). Those are read by `useUrlParameters` in classic mode only; they are
 * not synced on aniOS window URL build/parse.
 */

export const PLAYGROUND_OS_PARAM_KEYS = ["assets", "layout", "from"] as const;

export const PLAYGROUND_CHART_PARAM_KEYS = [
  "type",
  "period",
  "display",
  "rebase",
  "frequency",
  "statistic",
  "window",
  "start_date",
  "end_date",
  "exposure_display",
  "proxy_extend",
] as const;

export type PlaygroundOsLocation = {
  assets?: string;
  layout?: string;
  from?: string;
  period?: string;
  frequency?: string;
  display?: string;
  start_date?: string;
  end_date?: string;
};

export type ParsedPlaygroundAssets = {
  initialAssets: string[];
  initialDisplayNames: Record<string, string>;
  initialAssetMetadata: Record<string, ExtendedAssetInfo>;
};

/** @deprecated Import from `@/navigation/apps/playgroundLocation` — kept for route re-export. */
export function parseAssetsParam(assetsParam: string | null): ParsedPlaygroundAssets {
  return parsePlaygroundAssetsParam(assetsParam);
}

export function parsePlaygroundAssetsParam(assetsParam: string | null): ParsedPlaygroundAssets {
  if (!assetsParam) {
    return { initialAssets: [], initialDisplayNames: {}, initialAssetMetadata: {} };
  }

  try {
    const assetEntries: string[] = [];
    let currentEntry = "";
    const parts = assetsParam.split(",");

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      currentEntry += (currentEntry ? "," : "") + part;
      const colonCount = (currentEntry.match(/:::/g) || []).length;
      if (colonCount === 3) {
        assetEntries.push(currentEntry.trim());
        currentEntry = "";
      }
    }
    if (currentEntry) {
      assetEntries.push(currentEntry.trim());
    }

    const initialAssets: string[] = [];
    const initialDisplayNames: Record<string, string> = {};
    const initialAssetMetadata: Record<string, ExtendedAssetInfo> = {};

    assetEntries.forEach((entry) => {
      const [displayName, resourceId, resourceType, streamType] = entry.split(":::");
      if (resourceId) {
        initialAssets.push(resourceId);
        if (displayName) {
          initialDisplayNames[resourceId] = displayName;
        }
        initialAssetMetadata[resourceId] = {
          displayName: displayName || resourceId,
          resourceId,
          resourceType: resourceType || "product",
          streamType: (streamType || "default") as ExtendedAssetInfo["streamType"],
        };
      }
    });

    return { initialAssets, initialDisplayNames, initialAssetMetadata };
  } catch {
    return { initialAssets: [], initialDisplayNames: {}, initialAssetMetadata: {} };
  }
}

export function parsePlaygroundChartSettingsFromSearchParams(
  searchParams: URLSearchParams,
): Partial<ChartSettings> {
  const periodParam = searchParams.get("period");
  const displayModeParam = searchParams.get("display");
  const rebaseValueParam = searchParams.get("rebase");
  const frequencyParam = searchParams.get("frequency");
  const statisticParam = searchParams.get("statistic");
  const windowSizeParam = searchParams.get("window");
  const startDateParam = searchParams.get("start_date");
  const endDateParam = searchParams.get("end_date");
  const exposureDisplayModeParam = searchParams.get("exposure_display");
  const proxyExtendParam = searchParams.get("proxy_extend");

  const chartSettings: Partial<ChartSettings> = {};

  if (periodParam) {
    chartSettings.period = periodParam;
  }
  if (periodParam === "custom" && startDateParam && endDateParam) {
    chartSettings.customDateRange = [startDateParam, endDateParam];
  }
  if (displayModeParam === "index" || displayModeParam === "performance") {
    chartSettings.displayMode = displayModeParam;
  }
  if (rebaseValueParam) {
    const rebase = parseInt(rebaseValueParam, 10);
    if (!Number.isNaN(rebase) && rebase > 0) {
      chartSettings.rebaseValue = rebase;
    }
  }
  const validFrequencies = ["daily", "weekly", "monthly", "quarterly", "yearly"];
  if (frequencyParam && validFrequencies.includes(frequencyParam)) {
    chartSettings.frequency = frequencyParam as ChartSettings["frequency"];
  }
  const validStatistics = [
    "volatility",
    "returns",
    "sharpe_ratio",
    "sortino_ratio",
    "max_drawdown",
  ];
  if (statisticParam && validStatistics.includes(statisticParam)) {
    chartSettings.statistic = statisticParam as StatisticType;
  }
  if (windowSizeParam) {
    const window = parseInt(windowSizeParam, 10);
    if (!Number.isNaN(window) && window > 0) {
      chartSettings.windowSize = window;
    }
  }
  if (
    exposureDisplayModeParam &&
    ["weights", "scaled_weights", "values"].includes(exposureDisplayModeParam)
  ) {
    chartSettings.exposureDisplayMode = exposureDisplayModeParam as ChartSettings["exposureDisplayMode"];
  }
  if (proxyExtendParam === "1" || proxyExtendParam === "true") {
    chartSettings.extendIncompleteWithProxy = true;
  }

  return chartSettings;
}

export function parsePlaygroundTypeFromSearchParams(
  searchParams: URLSearchParams,
): PlaygroundType | undefined {
  const type = searchParams.get("type") as PlaygroundType | null;
  if (type && Object.values(PlaygroundType).includes(type)) {
    return type;
  }
  return undefined;
}

export function parsePlaygroundOsFromSearchParams(
  searchParams: URLSearchParams,
): PlaygroundOsLocation {
  const loc: PlaygroundOsLocation = {};
  const assets = searchParams.get("assets");
  if (assets) loc.assets = assets;
  const layout = searchParams.get("layout")?.trim();
  if (layout) loc.layout = layout;
  const from = searchParams.get("from")?.trim();
  if (from) loc.from = from;
  const period = searchParams.get("period")?.trim();
  if (period) loc.period = period;
  const frequency = searchParams.get("frequency")?.trim();
  if (frequency) loc.frequency = frequency;
  const display = searchParams.get("display")?.trim();
  if (display) loc.display = display;
  const startDate = searchParams.get("start_date")?.trim();
  if (startDate) loc.start_date = startDate;
  const endDate = searchParams.get("end_date")?.trim();
  if (endDate) loc.end_date = endDate;
  return loc;
}

export function parsePlaygroundOsFromOsParams(params: Record<string, string>): PlaygroundOsLocation {
  const loc: PlaygroundOsLocation = {};
  if (params.assets) loc.assets = params.assets;
  const layout = params.layout?.trim();
  if (layout) loc.layout = layout;
  const from = params.from?.trim();
  if (from) loc.from = from;
  if (params.period) loc.period = params.period;
  if (params.frequency) loc.frequency = params.frequency;
  if (params.display) loc.display = params.display;
  if (params.start_date) loc.start_date = params.start_date;
  if (params.end_date) loc.end_date = params.end_date;
  return loc;
}

export function serializePlaygroundOsToSearchParams(loc: PlaygroundOsLocation): URLSearchParams {
  const params = new URLSearchParams();
  if (loc.assets) params.set("assets", loc.assets);
  if (loc.layout) params.set("layout", loc.layout);
  if (loc.from) params.set("from", loc.from);
  if (loc.period) params.set("period", loc.period);
  if (loc.frequency) params.set("frequency", loc.frequency);
  if (loc.display) params.set("display", loc.display);
  if (loc.start_date) params.set("start_date", loc.start_date);
  if (loc.end_date) params.set("end_date", loc.end_date);
  return params;
}

export function serializePlaygroundOsToOsParams(loc: PlaygroundOsLocation): Record<string, string> {
  const out: Record<string, string> = {};
  const sp = serializePlaygroundOsToSearchParams(loc);
  sp.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

export function buildPlaygroundWindowUrl(loc: PlaygroundOsLocation): string {
  const qs = serializePlaygroundOsToSearchParams(loc).toString();
  return `/playground${qs ? `?${qs}` : ""}`;
}

export function playgroundChartSettingsFromOsLocation(
  loc: PlaygroundOsLocation,
): Partial<ChartSettings> {
  const fromUrl = new URLSearchParams();
  if (loc.period) fromUrl.set("period", loc.period);
  if (loc.frequency) fromUrl.set("frequency", loc.frequency);
  if (loc.display) fromUrl.set("display", loc.display);
  if (loc.start_date) fromUrl.set("start_date", loc.start_date);
  if (loc.end_date) fromUrl.set("end_date", loc.end_date);
  return parsePlaygroundChartSettingsFromSearchParams(fromUrl);
}

export const playgroundCodec: AppLocationCodec<PlaygroundOsLocation> = {
  appId: "playground",
  fromUrl: (_pathname, searchParams) => parsePlaygroundOsFromSearchParams(searchParams),
  fromParams: (params) => parsePlaygroundOsFromOsParams(params),
  toParams: (loc) => serializePlaygroundOsToOsParams(loc),
  toUrl: (loc) => buildPlaygroundWindowUrl(loc),
};
